import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Customer } from '@/lib/schemas';
import { formatDateTime } from '@/lib/utils';

const optOutResponseSchema = z.object({ whatsappOptOutAt: z.string().nullish() });

/**
 * הודעות WhatsApp אוטומטיות ללקוח. לקוח שהשיב "הסר" / STOP מוסר אוטומטית;
 * רק בעלים/מנהל מחזירים אותו (או מסירים ידנית). שליחה ידנית מהמסך לא מושפעת.
 */
export function WhatsAppOptOut({ customer }: { customer: Customer }) {
  const { t } = useTranslation(['customers', 'common']);
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const optedOutAt = customer.whatsappOptOutAt ?? null;

  const save = useMutation({
    mutationFn: (optedOut: boolean) =>
      request(`/customers/${customer.id}/whatsapp-opt-out`, {
        method: 'PUT',
        body: { optedOut },
        schema: optOutResponseSchema,
      }),
    onSuccess: (res) => {
      qc.setQueryData<Customer>(['customers', 'detail', customer.id], (prev) =>
        prev ? { ...prev, whatsappOptOutAt: res.whatsappOptOutAt ?? null } : prev,
      );
      toast.success(res.whatsappOptOutAt ? t('whatsapp.disabledToast') : t('whatsapp.enabledToast'));
    },
    onError: (err: unknown) =>
      toast.error(t('whatsapp.failed'), err instanceof ApiError ? err.message : t('form.unexpectedError')),
  });

  return (
    <label className="flex items-start justify-between gap-4">
      <span>
        <span className="block text-sm font-medium text-fg">{t('whatsapp.label')}</span>
        <span className="block text-xs text-fg-muted">
          {optedOutAt ? t('whatsapp.optedOut', { date: formatDateTime(optedOutAt) }) : t('whatsapp.enabledHint')}
        </span>
      </span>
      <Switch
        checked={!optedOutAt}
        disabled={!can('OWNER', 'MANAGER') || save.isPending}
        onCheckedChange={(enabled) => save.mutate(!enabled)}
        aria-label={t('whatsapp.label')}
      />
    </label>
  );
}
